import React, { useState } from "react";
import { FileText, Star, UploadCloud, X } from "lucide-react";
import { IoCloudUploadOutline } from "react-icons/io5";
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { useAuth } from "../../Context/ContextProvider";
import ReactMarkdown from 'react-markdown';
// CVReviewSection.jsx
// Tailwind-based React component for a CV Review (AI-powered) section.
// - Color scheme: #1A335D (dark blue), #94CB85 (green), white
// - Expects an endpoint POST /api/analyze-cv that accepts FormData { file: pdf }
//   and returns JSON: { rating: number(0-100), summary: string, strengths: [], improvements: [] }
// - If backend not available, the component will fallback to a mock response.

export default function ReviewSection() {
    const [file, setFile] = useState(null);
    const { base_url } = useAuth()
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [settings, setSettings] = useState({
        width: 300,
        height: 300,
        value: 78,
    });
    const handleFileChange = (e) => {
        setError(null);
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.type !== "application/pdf") {
            setError("Please upload a PDF file.");
            return;
        }
        setFile(f);
        setResult(null);
    };

    const removeFile = () => {
        setFile(null);
        setResult(null);
        setError(null);
    };

    const analyzeCV = async () => {
        if (!file) {
            setError("Please upload your CV first.");
            return;
        }
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("resume", file);

        try {
            const resp = await fetch(`${base_url}analyze-resume`, { method: "POST", body: formData });
            if (!resp.ok) throw new Error("Server error");
            const data = await resp.json();
            // Expected: { rating, summary, strengths, improvements }
            setLoading(false);
            setResult(data?.result);
            console.log(data)
        } catch (err) {
            // Fallback mock result if backend is not reachable.
            console.warn("Analysis failed, using mock result:", err);
            setLoading(false);
            // setResult({
            //     rating: 78,
            //     summary:
            //         "Your CV is strong in clinical experience and research, but could improve structure and keywords for ATS.",
            //     strengths: ["Clinical rotations clearly listed", "Research project included"],
            //     improvements: [
            //         "Add 2-3 measurable achievements under each role",
            //         "Use keywords from target job descriptions",
            //         "Shorten the profile summary to 2-3 lines"
            //     ]
            // });
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (score) => {
        // score is 0-100, map to 5 stars
        const stars = Math.round((score / 100) * 5);
        return (
            <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                        key={i}
                        size={18}
                        className={`${i < stars ? "text-[#94CB85]" : "text-white/40"}`}
                    />
                ))}
                <span className="ml-2 /80">{score}%</span>
            </div>
        );
    };

    return (
        <section className="cv_review max-w-4xl mx-auto my-12 p-6 rounded-2xl" >
            <div className="container">
                <div className="flex flex-col md:flex-row items-center gap-6">
                    <h3 className="home_page_section_title">CV Review (AI)</h3>
                    <p className="home_page_section_description">Upload your CV (PDF) and get instant AI-powered feedback & rating.</p>
                    <div className="flex-1 w-full">
                        {!result && <div className="upload_box">
                            <label className="cv_upload_labale" for="cvUpload">Upload CV <IoCloudUploadOutline style={{ fontSize: "16px", marginLeft: "2px" }} /></label>
                            <input onChange={handleFileChange} type="file" id="cvUpload" className="d-none" accept="application/pdf" />
                            <div className="help mt-2">{file ? file.name : "Drag & drop or click to upload PDF"}</div>
                            <div className="help">Max file size: 10MB • PDF only</div>
                            {loading && <div className="spinner-border text-primary mt-3" status="role"/>}
                            <p className="mt-3">“Only PDF files up to 10MB are supported. Our AI will scan your CV for clarity, keywords, and structure — then provide strengths, weaknesses, and actionable improvements.”</p>
                            {file && <button onClick={analyzeCV} disabled={loading}>{loading ? "Analyzing..." : "Review my CV"}</button>}
                        </div>}
                        {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
                        {result && (
                            <div className="mt-6 bg-white/5 border border-white/6 rounded-lg p-lg-5 p-2">
                                {/* <div className="row m-0">
                                    <div>
                                        <h4 className="text-lg font-semibold">AI Review Summary</h4>
                                        <p className="text-sm /75 mt-1">{result.summary}</p>
                                    </div>
                                    <div className="col-6">
                                        <div className="flex items-start justify-between gap-4">

                                            <div className="flex flex-col items-end">
                                                <div className="mb-3">{renderStars(result.rating)}</div>
                                                <div className="text-sm /70">Overall score</div>
                                            </div>
                                        </div>

                                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <h5 className="text-sm font-medium">Strengths</h5>
                                                <ul className="mt-2 space-y-2 text-sm">
                                                    {result.strengths?.map((s, i) => (
                                                        <li key={i} className="flex items-start gap-2">
                                                            <span className="inline-block w-2 h-2 rounded-full mt-2" style={{ background: "#94CB85" }} />
                                                            <span className="text-white/80">{s}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div>
                                                <h5 className="text-sm font-medium">Areas to Improve</h5>
                                                <ul className="mt-2 space-y-2 text-sm">
                                                    {result.improvements?.map((s, i) => (
                                                        <li key={i} className="flex items-start gap-2">
                                                            <span className="inline-block w-2 h-2 rounded-full mt-2 bg-[#f5a9a9]" />
                                                            <span className="text-white/80">{s}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className="mt-5 flex items-center gap-3">
                                            <button
                                                className="px-4 py-2 rounded-md font-semibold"
                                                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.08)" }}
                                                onClick={() => {
                                                    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement("a");
                                                    a.href = url;
                                                    a.download = "cv-review-summary.json";
                                                    a.click();
                                                    URL.revokeObjectURL(url);
                                                }}
                                            >
                                                Download Summary
                                            </button>

                                            <a href="#" className="ml-2 text-sm underline /80">Learn how to improve</a>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <Gauge
                                            {...settings}
                                            cornerRadius="50%"
                                            text={({ value }) => `${Math.round(value)}%`}
                                            sx={{
                                                [`& .${gaugeClasses.valueText}`]: {
                                                    fontSize: 35,
                                                    fill: '#1A335D',   // main text color
                                                    fontWeight: 600,
                                                },
                                                [`& .${gaugeClasses.valueText} tspan`]: {
                                                    fill: '#7e7e7eff',
                                                },
                                                [`& .${gaugeClasses.valueArc}`]: {
                                                    fill: '#94CB85',
                                                },
                                                [`& .${gaugeClasses.referenceArc}`]: {
                                                    fill: '#7e7e7eff',
                                                },
                                            }}
                                        />
                                    </div>
                                </div> */}
                                <div />
                                <ReactMarkdown>{result}</ReactMarkdown>


                            </div>
                        )}
                    </div>
                </div>
            </div>

        </section>
    );
}
